/**
 * Plain-DOM status card for the pre-bootstrap `/reset-local-data` path. The
 * app deliberately never boots there (see performLocalDataReset), so Angular
 * Material is unavailable; this hand-builds a centered card that matches the
 * site's theme (indigo backdrop, Roboto, mat-card surface and elevation, the
 * splash screen's brand spinner).
 */

const BACKDROP_STYLE = `
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #3f51b5;
  padding: 16px;
  z-index: 1000;
`;

const CARD_STYLE = `
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2),
              0 8px 10px 1px rgba(0, 0, 0, 0.14),
              0 3px 14px 2px rgba(0, 0, 0, 0.12);
  padding: 24px 32px 28px;
  max-width: 420px;
  text-align: center;
  font-family: Roboto, "Helvetica Neue", sans-serif;
  color: rgba(0, 0, 0, 0.87);
`;

const TITLE_STYLE = `
  margin: 16px 0 8px;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
`;

const MESSAGE_STYLE = `
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: rgba(0, 0, 0, 0.6);
  min-height: 44px;
`;

/**
 * The splash screen's spinner at card size. The `rotate`/`dash` keyframes are
 * defined globally in index.html, so only the per-element styles are inlined.
 */
const SPINNER_SVG = `
  <svg viewBox="25 25 50 50"
       style="width: 48px; height: 48px; margin-top: 20px; animation: rotate 2s linear infinite; transform-origin: center center;">
    <circle cx="50" cy="50" r="20" fill="none" stroke-width="3" stroke-miterlimit="10"
            style="stroke: #97ba3f; stroke-linecap: round; stroke-dasharray: 1, 200; stroke-dashoffset: 0; animation: dash 1.5s ease-in-out infinite;" />
  </svg>
`;

export interface ResetStatusCard {
  /** Updates the message for the number of other app tabs still open. */
  setOpenTabs(openTabs: number): void;
}

export interface ResetStatusCardLabels {
  title: string;
  workingMessage: string;
}

const DEFAULT_LABELS: ResetStatusCardLabels = {
  title: 'Resetting Local Data',
  workingMessage: 'Clearing locally stored data. You will be redirected shortly…',
};

export function renderResetStatusCard(
  labels: ResetStatusCardLabels = DEFAULT_LABELS,
  doc: Document = document,
): ResetStatusCard {
  // The app never boots on this path; replace the boot splash entirely.
  doc.querySelector('.real-background')?.remove();

  const backdrop = doc.createElement('div');
  backdrop.style.cssText = BACKDROP_STYLE;

  const card = doc.createElement('div');
  card.style.cssText = CARD_STYLE;
  backdrop.appendChild(card);

  const logo = doc.createElement('img');
  logo.src = 'assets/images/concrete-genius-logo-128x128.png';
  logo.alt = 'Concrete CloudOps';
  logo.style.cssText = 'width: 64px; height: 64px;';
  card.appendChild(logo);

  const title = doc.createElement('h1');
  title.style.cssText = TITLE_STYLE;
  title.textContent = labels.title;
  card.appendChild(title);

  const message = doc.createElement('p');
  message.style.cssText = MESSAGE_STYLE;
  card.appendChild(message);

  const spinner = doc.createElement('div');
  spinner.innerHTML = SPINNER_SVG;
  card.appendChild(spinner);

  doc.body.appendChild(backdrop);

  const setOpenTabs = (openTabs: number) => {
    message.textContent = openTabs > 0
      ? `Please close the ${openTabs} other Concrete CloudOps `
        + `${openTabs === 1 ? 'tab or window' : 'tabs or windows'} to continue.`
      : labels.workingMessage;
  };
  setOpenTabs(0);

  return { setOpenTabs };
}
